/**
 * Custom datepicker initialization module.
 *
 * This module provides proper date parsing for Flowbite datepickers,
 * preventing the year malformation bug where 2020 becomes 0020.
 * Includes real-time validation with user feedback.
 */

import { Datepicker } from "flowbite";

/** Minimum valid year for date validation. */
const MIN_YEAR = 1980;
/** Maximum valid year for date validation. */
const MAX_YEAR = 2050;

/** CSS class for error message elements. */
const ERROR_CLASS = "datepicker-error";

/** Store datepicker instances for later access (e.g., to fix corrupted state) */
const datepickerInstances = new WeakMap<HTMLInputElement, Datepicker>();

/** Track if recent change was from manual typing (vs widget click) */
const recentManualInput = new WeakMap<HTMLInputElement, boolean>();

/**
 * Parse an ISO date string (YYYY-MM-DD) into a Date object.
 * Validates that the year is within a reasonable range (1980-2050).
 *
 * @param dateStr - The date string to parse in YYYY-MM-DD format.
 * @returns A Date object if valid, or null if invalid or empty.
 */
function parseISODate(dateStr: string): Date | null {
  if (!dateStr) return null;

  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1; // JS months are 0-indexed
  const day = parseInt(match[3], 10);

  // Reject malformed years
  if (year < MIN_YEAR || year > MAX_YEAR) {
    console.warn(
      `[datepicker-init] Invalid year ${year} in date ${dateStr}. ` +
        `Year must be between ${MIN_YEAR} and ${MAX_YEAR}.`,
    );
    return null;
  }

  return new Date(year, month, day);
}

/**
 * Extract year from a date string for validation feedback.
 *
 * @param dateStr - The date string to extract year from.
 * @returns The year as a number, or null if invalid format.
 */
function extractYear(dateStr: string): number | null {
  if (!dateStr) return null;
  const match = dateStr.match(/^(\d{4})-\d{2}-\d{2}$/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Get or create the error message element for a datepicker input.
 *
 * @param input - The datepicker input element.
 * @returns The error message element.
 */
function getErrorElement(input: HTMLInputElement): HTMLElement {
  const container = input.closest(".relative");
  if (!container) {
    // Fallback: create error element as sibling
    let errorEl = input.nextElementSibling as HTMLElement | null;
    if (!errorEl?.classList.contains(ERROR_CLASS)) {
      errorEl = document.createElement("p");
      errorEl.className = ERROR_CLASS;
      input.insertAdjacentElement("afterend", errorEl);
    }
    return errorEl;
  }

  // Look for existing error element AFTER the container (as sibling)
  let errorEl = container.nextElementSibling as HTMLElement | null;
  if (!errorEl?.classList.contains(ERROR_CLASS)) {
    errorEl = document.createElement("p");
    errorEl.className = ERROR_CLASS;
    container.insertAdjacentElement("afterend", errorEl);
  }
  return errorEl;
}

/**
 * Show validation error for a datepicker input.
 *
 * @param input - The datepicker input element.
 * @param year - The invalid year value.
 */
function showError(input: HTMLInputElement, year: number): void {
  const errorEl = getErrorElement(input);
  errorEl.textContent = `Invalid year: ${year}. Year must be between ${MIN_YEAR} and ${MAX_YEAR}.`;
  errorEl.className = `${ERROR_CLASS} mt-1 text-sm text-red-600 dark:text-red-400`;
  errorEl.setAttribute("role", "alert");

  // Add error styling to input
  input.classList.add(
    "border-red-500",
    "dark:border-red-400",
    "focus:ring-red-500",
    "focus:border-red-500",
  );
  input.classList.remove(
    "border-gray-300",
    "dark:border-gray-600",
    "focus:ring-blue-500",
    "focus:border-blue-500",
  );
}

/**
 * Clear validation error for a datepicker input.
 *
 * @param input - The datepicker input element.
 */
function clearError(input: HTMLInputElement): void {
  const container = input.closest(".relative");
  const errorEl = container
    ? (container.nextElementSibling as HTMLElement | null)
    : (input.nextElementSibling as HTMLElement | null);

  if (errorEl?.classList.contains(ERROR_CLASS)) {
    errorEl.textContent = "";
    errorEl.className = ERROR_CLASS; // Reset to base class (removes mt-1, text styling)
    errorEl.removeAttribute("role");
  }

  // Restore normal styling
  input.classList.remove(
    "border-red-500",
    "dark:border-red-400",
    "focus:ring-red-500",
    "focus:border-red-500",
  );
  input.classList.add(
    "border-gray-300",
    "dark:border-gray-600",
    "focus:ring-blue-500",
    "focus:border-blue-500",
  );
}

/**
 * Validate the datepicker value and show/clear error accordingly.
 *
 * @param input - The datepicker input element.
 */
function validateDatepicker(input: HTMLInputElement): void {
  const value = input.value;
  if (!value) {
    clearError(input);
    return;
  }

  const year = extractYear(value);
  if (year === null) {
    // Non-standard format, let server-side validation handle it
    clearError(input);
    return;
  }

  if (year < MIN_YEAR || year > MAX_YEAR) {
    showError(input, year);
  } else {
    clearError(input);
  }
}

/**
 * Initialize all datepickers with proper date parsing and real-time validation.
 * Prevents Flowbite's auto-init from corrupting dates by taking control
 * of the initialization process and properly setting existing values.
 *
 * This function is idempotent - it will skip inputs that have already
 * been initialized.
 */
export function initDatepickers(): void {
  const inputs = document.querySelectorAll<HTMLInputElement>("[data-datepicker-init]");

  inputs.forEach((input) => {
    // Skip if already initialized
    if (input.dataset.datepickerInitialized === "true") return;

    const format = input.dataset.datepickerFormat || "yyyy-mm-dd";
    const existingValue = input.dataset.datepickerValue || input.value;

    // Create datepicker instance with our configuration
    const datepicker = new Datepicker(input, {
      format,
      autohide: true,
    });

    // Store instance for later access (e.g., to fix corrupted state)
    datepickerInstances.set(input, datepicker);

    // If there's an existing value, set it properly using the parsed Date object
    // This prevents Flowbite from incorrectly parsing the string value
    if (existingValue) {
      const parsedDate = parseISODate(existingValue);
      if (parsedDate) {
        datepicker.setDate(parsedDate);
      }
    }

    // Track manual typing to distinguish from widget clicks
    // input event fires for typing, not for clicking calendar days
    input.addEventListener("input", () => {
      recentManualInput.set(input, true);
      // Clear flag after short delay (after changeDate would fire)
      setTimeout(() => recentManualInput.set(input, false), 50);
    });

    // Add change event listener for real-time validation and year auto-correction
    // Flowbite dispatches 'changeDate' custom event when date is selected
    input.addEventListener("changeDate", () => {
      const value = input.value;
      const year = extractYear(value);
      const wasManualInput = recentManualInput.get(input) === true;

      // Only auto-correct Flowbite corruption pattern (year < 100) on widget click
      // Don't auto-correct other out-of-range years (like 2100) - let validation show error
      if (!wasManualInput && year !== null && year < 100) {
        const match = value.match(/^\d{4}-(\d{2})-(\d{2})$/);
        if (match) {
          // Infer intended year: 0025 → 2025, 0020 → 2020
          const inferredYear = year + 2000;

          // Only correct if inferred year is valid
          if (inferredYear >= MIN_YEAR && inferredYear <= MAX_YEAR) {
            const correctedDate = new Date(
              inferredYear,
              parseInt(match[1], 10) - 1,
              parseInt(match[2], 10),
            );

            // Update both input and datepicker internal state
            const dp = datepickerInstances.get(input);
            if (dp) {
              dp.setDate(correctedDate);
            }
          }
        }
      }

      validateDatepicker(input);
    });

    // Also validate on blur in case user types manually
    input.addEventListener("blur", () => {
      validateDatepicker(input);
    });

    // Validate existing value on init (shows error if pre-populated with bad date)
    if (existingValue) {
      validateDatepicker(input);
    }

    // Mark as initialized to prevent double-init
    input.dataset.datepickerInitialized = "true";
  });
}

// Initialize on DOM ready
document.addEventListener("DOMContentLoaded", initDatepickers);

// Re-initialize after HTMX swaps (for dynamic content loaded via AJAX)
document.addEventListener("htmx:afterSettle", initDatepickers);
